"use client";

import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState } from "react";

interface AuthGuardProps {
  children: React.ReactNode;
}

const PUBLIC_PATHS = [
  "/",
  "/login",
  "/register",
  "/logout",
  "/verify-email",
  "/login/error",
  "/register/error",
  "/debug",
];

export default function AuthGuard({ children }: AuthGuardProps) {
  const { data: session, status } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);

  const isPublicPath = PUBLIC_PATHS.some((p) => pathname.startsWith(p));

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || status === "loading") return;
    
    // Special handling for root path
    if (pathname === "/") {
      // Don't redirect, let the root page handle its own redirect logic
      return;
    }
    
    if (!isPublicPath && !session) {
      router.replace("/login");
    }
  }, [session, status, router, pathname, mounted, isPublicPath]);

  // Public routes: same tree on server and client (no localStorage / session gates)
  if (isPublicPath) {
    return <>{children}</>;
  }

  // Protected routes: hold a consistent loading UI until mounted + session resolved
  if (!mounted || status === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#fff5f0" }}
      >
        <div className="text-center">
          <div className="text-6xl mb-4">👵</div>
          <p className="text-amber-700">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return <>{children}</>;
}
